/// IconButton: Small icon button with tooltip for toolbar and panel actions.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Tooltip } from "./Tooltip";

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Icon element to render inside the button. */
  icon: ReactNode;
  /** Accessible label shown as tooltip. */
  label: string;
  /** Whether the button appears in active/pressed state. */
  active?: boolean;
  /** Button size variant. */
  size?: "sm" | "md";
}

/**
 * Small icon button with optional tooltip.
 * Used throughout toolbar, tab bar, and panels.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, active = false, size = "md", className = "", disabled, ...props },
  ref,
) {
  const sizeClasses = size === "sm" ? "h-6 w-6" : "h-7 w-7";

  const baseClasses = [
    "inline-flex items-center justify-center rounded",
    "transition-colors duration-100",
    "focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-500",
    sizeClasses,
  ].join(" ");

  const stateClasses = disabled
    ? "text-zinc-600 cursor-not-allowed"
    : active
      ? "bg-blue-600 text-white hover:bg-blue-700"
      : "text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200";

  return (
    <Tooltip content={label}>
      <button
        ref={ref}
        type="button"
        aria-label={label}
        disabled={disabled}
        className={`${baseClasses} ${stateClasses} ${className}`}
        {...props}
      >
        {icon}
      </button>
    </Tooltip>
  );
});
